/**
 * API服务层
 */
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  ApiResponse,
  QueryRequest,
  QueryResult,
  SQLQueryRequest,
  QuerySuggestion,
  SystemInfo,
  LoginRequest,
  LoginResponse,
  HealthCheck,
} from '../types/api';

class ApiService {
  private client: AxiosInstance;
  private fallbackClient?: AxiosInstance;
  private proxyClient?: AxiosInstance;
  private currentBaseURL: string;
  private fallbackBaseURL?: string;
  private proxyBaseURL?: string;
  private sslFallbackEnabled: boolean;
  private useProxyFallback: boolean;

  constructor() {
    // 在生产环境中直接使用Cloudflare Workers API URL
    const isProduction = window.location.hostname.includes('pages.dev') || window.location.hostname.includes('chunxueguanlibu.top');

    this.currentBaseURL = isProduction
      ? 'https://cost-analysis-api-ts.qu18354531302.workers.dev/api'
      : (import.meta.env.VITE_API_BASE_URL || '/api');

    this.fallbackBaseURL = isProduction
      ? 'https://cost-analysis-api-ts.qu18354531302.workers.dev/api'
      : import.meta.env.VITE_API_FALLBACK_URL;

    this.proxyBaseURL = import.meta.env.VITE_API_PROXY_URL;
    this.sslFallbackEnabled = import.meta.env.VITE_ENABLE_SSL_FALLBACK === 'true';
    this.useProxyFallback = import.meta.env.VITE_USE_PROXY_FALLBACK === 'true';

    console.log('🔧 API Configuration:', {
      isProduction,
      currentBaseURL: this.currentBaseURL,
      fallbackBaseURL: this.fallbackBaseURL,
      hostname: window.location.hostname
    });

    this.client = this.createAxiosInstance(this.currentBaseURL);

    // Create fallback client if fallback URL is provided (HTTPS only to avoid Mixed Content)
    if (this.fallbackBaseURL && this.sslFallbackEnabled && this.fallbackBaseURL.startsWith('https://')) {
      this.fallbackClient = this.createAxiosInstance(this.fallbackBaseURL);
    }

    // Create proxy client for same-origin requests (avoids Mixed Content)
    if (this.proxyBaseURL && this.useProxyFallback) {
      this.proxyClient = this.createAxiosInstance(this.proxyBaseURL);
    }

    // Setup interceptors for main client
    this.setupInterceptors(this.client);

    // Setup interceptors for fallback client if it exists
    if (this.fallbackClient) {
      this.setupInterceptors(this.fallbackClient);
    }

    // Setup interceptors for proxy client if it exists
    if (this.proxyClient) {
      this.setupInterceptors(this.proxyClient);
    }
  }

  private createAxiosInstance(baseURL: string, additionalConfig?: any): AxiosInstance {
    return axios.create({
      baseURL,
      timeout: 45000, // Increased to 45 seconds to allow backend processing
      headers: {
        'Content-Type': 'application/json',
      },
      ...additionalConfig
    });
  }

  private setupInterceptors(client: AxiosInstance) {
    // 请求拦截器
    client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Token过期，清除本地存储
          localStorage.removeItem('access_token');
          localStorage.removeItem('user_info');

          // 只有在不是登录页面时才跳转，避免循环重定向
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Enhanced request method with multiple fallback strategies
  private async makeRequest<T>(
    requestFn: (client: AxiosInstance) => Promise<AxiosResponse<T>>,
    retryWithFallback: boolean = true
  ): Promise<AxiosResponse<T>> {
    try {
      // Try main client first
      return await requestFn(this.client);
    } catch (error: any) {
      if (!this.shouldUseFallback(error) || !retryWithFallback) {
        throw error;
      }

      console.warn('SSL/Network error detected, trying fallback strategies:', error.message);

      // Strategy 1: Try HTTPS fallback client (if available and different from main)
      if (this.fallbackClient && this.fallbackBaseURL !== this.currentBaseURL) {
        try {
          console.log('Trying HTTPS fallback client...');
          return await requestFn(this.fallbackClient);
        } catch (fallbackError: any) {
          console.warn('HTTPS fallback failed:', fallbackError.message);
        }
      }

      // Strategy 2: Try proxy/same-origin client (avoids Mixed Content)
      if (this.proxyClient) {
        try {
          console.log('Trying proxy/same-origin client...');
          return await requestFn(this.proxyClient);
        } catch (proxyError: any) {
          console.warn('Proxy fallback failed:', proxyError.message);
        }
      }

      // Strategy 3: Try with different request configuration
      try {
        console.log('Trying with modified request configuration...');
        const modifiedClient = this.createAxiosInstance(this.currentBaseURL, {
          timeout: 60000, // Longer timeout
          httpsAgent: false, // Disable HTTPS agent
        });
        this.setupInterceptors(modifiedClient);
        return await requestFn(modifiedClient);
      } catch (modifiedError: any) {
        console.warn('Modified configuration failed:', modifiedError.message);
      }

      console.error('All fallback strategies failed');
      throw error; // Return original error
    }
  }

  private shouldUseFallback(error: any): boolean {
    if (!this.sslFallbackEnabled) {
      return false;
    }

    const errorCode = error.code;
    const errorMessage = error.message?.toLowerCase() || '';

    // Check for SSL/TLS related errors and Mixed Content errors
    const networkErrors = [
      'ERR_SSL_PROTOCOL_ERROR',
      'ERR_CERT_AUTHORITY_INVALID',
      'ERR_CERT_COMMON_NAME_INVALID',
      'ERR_SSL_VERSION_OR_CIPHER_MISMATCH',
      'ERR_NETWORK',
      'ERR_BLOCKED_BY_CLIENT',
      'ERR_MIXED_CONTENT',
      'NETWORK_ERROR'
    ];

    const errorPatterns = [
      'ssl',
      'certificate',
      'network error',
      'mixed content',
      'blocked',
      'connection refused',
      'timeout'
    ];

    return networkErrors.includes(errorCode) ||
           errorPatterns.some(pattern => errorMessage.includes(pattern));
  }

  // 健康检查
  async healthCheck(): Promise<HealthCheck> {
    return this.makeRequest(async (client) => {
      // Use absolute URL for health check since it's at root level
      const baseUrl = client.defaults.baseURL || '/api';
      const healthUrl = baseUrl.replace('/api', '/health');
      return await axios.get(healthUrl, { timeout: 10000 });
    }).then(response => response.data);
  }

  // 系统信息
  async getSystemInfo(): Promise<SystemInfo> {
    const response = await this.makeRequest(async (client) => {
      return await client.get('/query/info');
    });
    // Backend returns wrapped response: { success: true, data: SystemInfo }
    return response.data.data || response.data;
  }

  // 自然语言查询
  async naturalLanguageQuery(request: QueryRequest): Promise<QueryResult> {
    const response = await this.makeRequest(async (client) => {
      return await client.post('/query/natural-language', request, {
        timeout: 60000, // 60 seconds for AI processing
        headers: {
          'Content-Type': 'application/json'
        }
      });
    });
    // Backend returns wrapped response: { success: true, data: QueryResult }
    return response.data.data || response.data;
  }

  // 直接SQL查询
  async directSQLQuery(request: SQLQueryRequest): Promise<QueryResult> {
    const response = await this.client.post('/query/sql', request);
    // Backend returns wrapped response: { success: true, data: QueryResult }
    return response.data.data || response.data;
  }

  // 获取查询建议
  async getQuerySuggestions(partialQuery: string = ''): Promise<QuerySuggestion> {
    const response = await this.client.get('/query/suggestions', {
      params: { q: partialQuery },
    });
    // Backend returns wrapped response: { success: true, data: QuerySuggestion }
    return response.data.data || response.data;
  }

  // 测试查询系统
  async testQuerySystem(): Promise<ApiResponse> {
    const response = await this.client.get('/query/test');
    // Backend returns wrapped response: { success: true, data: TestResult }
    return response.data.data || response.data;
  }

  // 用户认证
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await this.client.post('/auth/login', request);
    // Backend returns wrapped response: { success: true, data: LoginResponse }
    const loginData = response.data.data || response.data;

    // Handle backend response format: backend returns 'token' but frontend expects 'access_token'
    if (loginData.token && !loginData.access_token) {
      loginData.access_token = loginData.token;
      loginData.token_type = 'bearer';
    }

    return loginData;
  }

  // 用户注册
  async register(request: LoginRequest): Promise<ApiResponse> {
    const response = await this.client.post('/auth/register', request);
    // Backend returns wrapped response: { success: true, data: RegisterResponse }
    return response.data.data || response.data;
  }

  // 获取当前用户信息
  async getCurrentUser(): Promise<ApiResponse> {
    const response = await this.client.get('/auth/me');
    // Backend returns wrapped response: { success: true, data: UserInfo }
    return response.data.data || response.data;
  }

  // 通用GET请求
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response = await this.client.get(url, { params });
    // Backend returns wrapped response: { success: true, data: T }
    return response.data.data || response.data;
  }

  // 通用POST请求
  async post<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.post(url, data);
    // Backend returns wrapped response: { success: true, data: T }
    return response.data.data || response.data;
  }

  // 通用PUT请求
  async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await this.client.put(url, data);
    // Backend returns wrapped response: { success: true, data: T }
    return response.data.data || response.data;
  }

  // 通用DELETE请求
  async delete<T = any>(url: string): Promise<T> {
    const response = await this.client.delete(url);
    // Backend returns wrapped response: { success: true, data: T }
    return response.data.data || response.data;
  }
}

// 创建单例实例
export const apiService = new ApiService();
export default apiService;
